import tokensFrom, { alpha, mix, readableOn, textShade, contrastOf } from './tokensFrom';
/**
 * The RoRi blue design system, expressed as a MUI theme.
 *
 * Tokens are taken from the RoRi blue design guide.
 *
 * The app supports both modes: dark is the guide's default navy palette, while
 * light swaps to its pale blue surfaces and dark ink. Blue and cyan stay fixed.
 */

// --- Tokens, straight from the guide ---------------------------------------

export const tokens = {
    // Surfaces
    bg: '#07111F',
    bg2: '#0B1728',
    bg3: '#10233A',
    navySurface: '#13263F',
    navy: '#163A5C',
    paper: '#F5F9FD',

    // Brand blue
    p50: '#EFF6FF',
    p100: '#DBEAFE',
    p200: '#BFDBFE',
    p300: '#93C5FD',
    p400: '#60A5FA',
    p500: '#3B82F6',
    p600: '#2563EB',
    p700: '#1D4ED8',
    p800: '#1E3A8A',

    // Cyan accent
    c300: '#8BDCF5',
    c400: '#38BDF8',
    c500: '#0EA5E9',
    c600: '#0284C7',

    // Ink, on dark
    ink: '#F1F7FF',
    ink2: '#C7D6E8',
    ink3: '#8FA6BE',
    ink4: '#5F7894',

    // Ink on the light surface.
    inkOnPaper: '#0D1B2A',
    inkOnPaper2: '#344B63',
    inkOnPaper3: '#607A94',

    gradient1: 'linear-gradient(135deg, #38BDF8 0%, #60A5FA 50%, #2563EB 100%)',
    gradient2: 'linear-gradient(120deg, #FFFFFF 0%, #DCEEFF 35%, #93C5FD 70%, #38BDF8 100%)',

    // Radii
    r1: 6,
    r2: 10,
    r3: 14,
    r4: 20,
    r5: 28,

    // Motion
    easing: 'cubic-bezier(.2,.7,.3,1)',
    fast: 160,
    med: 240,

    maxWidth: 1320,

    // The fallbacks a guide overrides. `success` and `warning` live here
    // because the palette now reads them from the token set, and the base set
    // is what is used before any guide has loaded — without them the first
    // paint hands MUI an undefined colour.
    danger: '#F87171',
    success: '#4ADE80',
    warning: '#F59E0B'
};

const lines = (mode) =>
    mode === 'dark'
        ? { soft: 'rgba(255,255,255,0.07)', strong: 'rgba(255,255,255,0.16)', field: 'rgba(255,255,255,0.16)' }
        : { soft: 'rgba(13,27,42,0.08)', strong: 'rgba(13,27,42,0.18)', field: 'rgba(13,27,42,0.18)' };

export const FONT_SANS = "'IBM Plex Sans', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
export const FONT_MONO = "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
export const FONT_SERIF = "'Instrument Serif', Georgia, serif";

/**
 * Mono, uppercase, wide-tracked. The guide uses this for every label, eyebrow
 * and piece of metadata, and it is most of what makes the system recognisable.
 */
/**
 * Hide a scrollbar without disabling scrolling.
 *
 * The element still scrolls by wheel, trackpad, keyboard (arrows, Page Up and
 * Down, Space) and programmatically — only the painted bar goes. Worth knowing:
 * this also removes the visual cue that there is more content below, so it is
 * applied to the page chrome and the nav, not to every scrollable box.
 */
export const hideScrollbar = {
    scrollbarWidth: 'none', // Firefox
    msOverflowStyle: 'none', // legacy Edge
    '&::-webkit-scrollbar': { width: 0, height: 0, display: 'none' }
};

/**
 * A family from the guide, in front of the stack it replaces.
 *
 * Kept as a stack rather than a bare name so a font that has not loaded yet —
 * or at all — falls back to something readable instead of to the browser's
 * default serif.
 */
const fontStack = (family, fallback) =>
    (family && !fallback.includes(`'${family}'`) ? `'${family}', ${fallback}` : fallback);

const eyebrow = {
    fontFamily: FONT_MONO,
    fontWeight: 500,
    fontSize: 12,
    letterSpacing: '0.12em',
    textTransform: 'uppercase'
};

/**
 * The color an employer chose, as it is drawn: moved towards black on a light page, or towards white on a dark one, until it can be read
 * as text there (4.5:1), and no further. A pale yellow chosen for a button becomes a legible olive in links, and still looks like itself.
 */
export const readableAccent = (color, dark) => {
    const page = dark ? tokens.bg : '#FFFFFF';
    const toward = dark ? '#FFFFFF' : '#000000';
    for (let step = 0; step <= 20; step += 1) {
        const candidate = mix(color, toward, step * 0.05);
        if (contrastOf(candidate, page) >= 4.5) return candidate;
    }
    return toward;
};

/** The blue and cyan tokens, made from one color: a lighter and a darker run of it, and the gradient the buttons and rings use. */
const accentTokens = (color, dark) => {
    const main = readableAccent(color, dark);
    const lighter = (amount) => mix(main, '#FFFFFF', amount);
    const darker = (amount) => mix(main, '#000000', amount);
    return {
        p300: lighter(0.5), p400: lighter(0.25), p500: main, p600: darker(0.15), p700: darker(0.3), p800: darker(0.45),
        c300: lighter(0.4), c400: main, c500: main, c600: darker(0.15),
        gradient1: `linear-gradient(135deg, ${lighter(0.25)} 0%, ${main} 55%, ${darker(0.15)} 100%)`
    };
};

/**
 * The theme, for a mode and — optionally — a design guide other than blue.
 *
 * The guide arrives already parsed and reduced to a handful of colours; every
 * token it does not supply is computed from the ones it does, and anything
 * still missing falls back to the blue set below. So a theme is always
 * complete, whatever the guide happened to name.
 */
export const buildTheme = (mode, appearance = null, accent = null) => {
    const dark = mode === 'dark';
    // An employer's own color, where it has chosen one, stands in for the blue and the cyan. It is moved only as far as reading it needs.
    const t = { ...tokensFrom(tokens, appearance), ...(accent ? accentTokens(accent, dark) : {}) };

    // A guide that declares how components are shaped, not only coloured.
    // Null for every guide that predates that, and then nothing below differs
    // from what the application did before: the `soft` branches are all behind
    // this one flag. A soft guide is light only, so `dark` is never true with it.
    const ui = !dark && t.shape ? t.shape : null;
    const soft = Boolean(ui);
    const neutral = t.neutral || {};

    const line = soft && neutral.border
        ? {
            soft: neutral.border,
            strong: mix(neutral.border, t.inkOnPaper, 0.2),
            // A field's outline is what tells somebody where to type, so it has
            // to be seen (3:1), which the guide's hairline border is not.
            field: mix(neutral.border, t.inkOnPaper, 0.42)
        }
        : lines(mode);

    const surface = dark ? t.bg : t.paper;
    const surface2 = dark ? t.bg2 : '#FFFFFF';
    const surface3 = dark ? t.bg3 : soft && neutral.subtle ? neutral.subtle : '#E8F0F7';

    const textPrimary = dark ? t.ink : t.inkOnPaper;
    const textSecondary = dark ? t.ink2 : t.inkOnPaper2;
    // Supporting text sits on the subtle surface too (table heads, the nav), a
    // shade where the guide's own grey falls just short of 4.5:1.
    const textMeta = dark
        ? t.ink3
        : soft && neutral.muted
            ? (contrastOf(neutral.muted, surface3) >= 4.5 ? neutral.muted : textShade(neutral.muted, surface3))
            : t.inkOnPaper3;

    const primaryMain = t.p500;
    const accentMain = t.c400;
    // What shows where something is focused or active. The older guides use
    // the cyan accent; a guide can ask for its primary instead.
    const focusMain = soft && ui.focus === 'primary' ? t.p500 : t.c400;

    // Status colours. Each works as a fill; as text each is darkened only as
    // far as reading needs, against the pale tint it sits on in a chip.
    const tintOf = (colour) => mix(colour, '#FFFFFF', 0.86);
    const statusColours = soft
        ? { primary: t.p500, secondary: accentMain, success: t.success, warning: t.warning, error: t.danger, info: accentMain }
        : null;
    const softPalette = (colour) => ({
        main: colour,
        dark: textShade(colour, '#FFFFFF'),
        contrastText: readableOn(colour, t.inkOnPaper)
    });

    // The face behind labels and metadata. Mono for the guides that use it,
    // the body face for one that asks for plain labels.
    const plainLabels = soft && ui.labels === 'plain';
    const sansStack = fontStack(t.fontSans, FONT_SANS);
    const monoStack = plainLabels ? sansStack : FONT_MONO;
    const label = plainLabels
        ? { fontFamily: sansStack, fontWeight: 500, fontSize: 12, letterSpacing: '0.02em', textTransform: 'none', lineHeight: 1.5 }
        : eyebrow;

    return {
        palette: {
            mode,
            primary: {
                main: primaryMain,
                light: dark ? t.p400 : t.p500,
                dark: dark ? t.p700 : t.p800,
                // White on the guide's blue; on an employer's color, whichever of white and ink reads better.
                contrastText: accent ? readableOn(primaryMain, t.inkOnPaper) : '#FFFFFF'
            },
            secondary: soft
                ? { ...softPalette(accentMain), light: t.c300 }
                : {
                    main: accentMain,
                    light: t.c300,
                    dark: t.c600,
                    contrastText: t.bg
                },
            error: soft ? softPalette(t.danger) : { main: dark ? t.danger : '#C7362F' },
            // Taken from the guide, not written down here.
            //
            // `tokensFrom` has carried these two since guides were parsed, and
            // this file threw them away — so a guide that defined its own
            // green got #4ADE80 anyway. Nothing moved while the only guides
            // were Blue and Green, which define neither and fall back to
            // exactly these values; the two newer guides do define them, and
            // their success is a deeper green that also reads far better on a
            // light background (3.1:1 against white, where #4ADE80 manages
            // 1.65:1).
            warning: soft ? softPalette(t.warning) : { main: t.warning },
            success: soft ? softPalette(t.success) : { main: t.success },
            info: soft ? softPalette(accentMain) : { main: accentMain },
            background: { default: surface, paper: surface2 },
            text: { primary: textPrimary, secondary: textSecondary, disabled: textMeta },
            divider: line.soft,
            // Not part of MUI's contract; read by components that need the
            // guide's own names rather than MUI's approximations.
            dataflix: {
                surface,
                surface2,
                surface3,
                line: line.soft,
                lineStrong: line.strong,
                meta: textMeta,
                accent: focusMain,
                gradient1: t.gradient1,
                gradient2: t.gradient2,
                eyebrow: label,
                mono: monoStack,
                serif: FONT_SERIF,
                // One palette for every chart, in the guide's order, so a series
                // keeps its colour from the radar to a bar. Only a guide that
                // declares one has it; charts fall back to their own otherwise.
                ...(soft ? { series: [t.p500, accentMain, t.success, t.warning] } : {}),
                // The nav is the guide's darkest surface in both modes: it is
                // the frame around the page, not part of it. A soft guide puts
                // its navigation on the subtle surface instead.
                navBg: dark ? t.bg2 : soft && neutral.subtle ? surface3 : surface2,
                navText: textPrimary,
                navMeta: textMeta
            }
        },

        shape: { borderRadius: soft ? ui.radius.input : t.r2 },

        typography: soft ? {
            fontFamily: sansStack,
            // The guide's scale: page title 28-32 bold, section heading 20-24
            // semibold, body 14-16, metadata 12-13. Medium and semibold carry the
            // hierarchy; not everything is bold.
            h1: { fontSize: 'clamp(28px, 3vw, 32px)', lineHeight: 1.15, letterSpacing: '-0.02em', fontWeight: 700 },
            h2: { fontSize: 28, lineHeight: 1.2, letterSpacing: '-0.02em', fontWeight: 700 },
            h3: { fontSize: 24, lineHeight: 1.25, letterSpacing: '-0.015em', fontWeight: 600 },
            h4: { fontSize: 'clamp(26px, 2.6vw, 30px)', lineHeight: 1.2, letterSpacing: '-0.02em', fontWeight: 700 },
            h5: { fontSize: 22, lineHeight: 1.25, letterSpacing: '-0.01em', fontWeight: 600 },
            h6: { fontSize: 18, lineHeight: 1.3, letterSpacing: '-0.005em', fontWeight: 600 },
            subtitle1: { fontSize: 16, lineHeight: 1.5, fontWeight: 600 },
            subtitle2: { fontSize: 14, lineHeight: 1.45, fontWeight: 600 },
            body1: { fontSize: 15, lineHeight: 1.6 },
            body2: { fontSize: 14, lineHeight: 1.55 },
            caption: { fontFamily: sansStack, fontSize: 12.5, lineHeight: 1.5, letterSpacing: 0 },
            overline: label,
            button: { fontWeight: 600, fontSize: 14.5, textTransform: 'none', letterSpacing: 0 }
        } : {
            fontFamily: fontStack(t.fontSans, FONT_SANS),
            // clamp() from the guide's type scale, so headings scale fluidly
            // between phone and desktop instead of stepping at breakpoints.
            h1: { fontSize: 'clamp(32px, 5vw, 56px)', lineHeight: 1.05, letterSpacing: '-0.03em', fontWeight: 500 },
            h2: { fontSize: 30, lineHeight: 1.15, letterSpacing: '-0.02em', fontWeight: 500 },
            h3: { fontSize: 22, lineHeight: 1.25, letterSpacing: '-0.01em', fontWeight: 500 },
            h4: { fontSize: 19, lineHeight: 1.3, fontWeight: 500 },
            h5: { fontSize: 20, lineHeight: 1.2, letterSpacing: '-0.02em', fontWeight: 500 },
            h6: { fontSize: 17, lineHeight: 1.25, letterSpacing: '-0.015em', fontWeight: 500 },
            subtitle1: { fontSize: 16, lineHeight: 1.5, fontWeight: 500 },
            subtitle2: { fontSize: 14.5, lineHeight: 1.45, fontWeight: 500 },
            body1: { fontSize: 15, lineHeight: 1.6 },
            body2: { fontSize: 14, lineHeight: 1.55 },
            caption: { fontFamily: fontStack(t.fontMono, FONT_MONO), fontSize: 11, letterSpacing: '0.08em' },
            overline: eyebrow,
            button: { fontWeight: 500, fontSize: 15, textTransform: 'none', letterSpacing: 0 }
        },

        transitions: {
            easing: { easeInOut: t.easing, easeOut: t.easing, easeIn: t.easing },
            duration: { shortest: t.fast, shorter: t.fast, short: t.med }
        },

        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    body: {
                        backgroundColor: surface,
                        color: textPrimary,
                        // Stats and metrics line up in columns.
                        fontFeatureSettings: "'tnum'",
                        // The page still scrolls; the grey gutter down the side
                        // of every screen does not belong in this design.
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none'
                    },
                    'html': { scrollbarWidth: 'none', msOverflowStyle: 'none' },
                    'html::-webkit-scrollbar, body::-webkit-scrollbar': {
                        width: 0,
                        height: 0,
                        display: 'none'
                    },
                    // The guide's accessibility rule: a 2px cyan ring at 2px
                    // offset on everything focusable, never outline:none alone.
                    '*:focus-visible': {
                        outline: `2px solid ${focusMain}`,
                        outlineOffset: 2
                    },
                    'h1, h2': { textWrap: 'balance' }
                }
            },

            // Every button is a pill in this system, whatever its variant —
            // unless the guide says its buttons are rounded rectangles.
            MuiButton: soft ? {
                defaultProps: { disableElevation: true },
                styleOverrides: {
                    root: {
                        borderRadius: ui.radius.button,
                        padding: '10px 20px',
                        minHeight: ui.controlHeight,
                        transition: `background-color ${t.fast}ms ${t.easing}, border-color ${t.fast}ms ${t.easing}`,
                        '&.Mui-disabled': { opacity: 0.45 }
                    },
                    sizeSmall: { padding: '6px 14px', fontSize: 13.5, minHeight: 36 },
                    sizeLarge: { minHeight: Math.min(ui.controlHeight + 4, 48) },
                    outlined: { borderColor: line.field, backgroundColor: 'transparent' }
                }
            } : {
                defaultProps: { disableElevation: true },
                styleOverrides: {
                    root: {
                        borderRadius: 999,
                        padding: '10px 20px',
                        transition: `transform ${t.fast}ms ${t.easing}, background-color ${t.fast}ms ${t.easing}`,
                        '&:hover': { transform: 'translateY(-1px)' },
                        '&:active': { transform: 'translateY(0)' },
                        '&.Mui-disabled': { opacity: 0.4, transform: 'none' }
                    },
                    sizeSmall: { padding: '6px 14px', fontSize: 13.5 },
                    contained: {
                        '&:hover': { backgroundColor: t.p400 }
                    },
                    outlined: {
                        borderColor: line.strong,
                        backgroundColor: dark ? 'rgba(255,255,255,0.04)' : 'rgba(13,27,42,0.04)'
                    }
                }
            },

            MuiIconButton: {
                styleOverrides: {
                    root: { transition: `transform ${t.fast}ms ${t.easing}` }
                }
            },

            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none',
                        border: `1px solid ${line.soft}`
                    },
                    // Menus and dialogs sit above the page, so they take the
                    // raised surface rather than the page surface.
                    elevation0: { border: 'none' },
                    ...(soft ? {
                        rounded: { borderRadius: ui.radius.card },
                        // Minimal shadow: the border does the separating.
                        elevation1: { boxShadow: `0 1px 2px ${alpha(t.inkOnPaper, 0.05)}` }
                    } : {})
                }
            },

            MuiCard: {
                styleOverrides: {
                    root: soft ? {
                        borderRadius: ui.radius.card,
                        border: `1px solid ${line.soft}`,
                        boxShadow: `0 1px 2px ${alpha(t.inkOnPaper, 0.05)}`
                    } : {
                        borderRadius: t.r3,
                        border: `1px solid ${line.soft}`,
                        transition: `border-color ${t.med}ms ${t.easing}, transform ${t.med}ms ${t.easing}`,
                        // The guide's hover rule: lift and reveal the strong
                        // line. No shadow change — the lift alone carries it.
                        '&:hover': { borderColor: line.strong }
                    }
                }
            },

            // Cards breathe: 20-24px, not MUI's 16.
            ...(soft ? {
                MuiCardContent: {
                    styleOverrides: { root: { padding: 20, '&:last-child': { paddingBottom: 20 } } }
                }
            } : {}),

            MuiChip: {
                styleOverrides: {
                    root: { borderRadius: 999, fontWeight: 500 },
                    // Tags are mono uppercase throughout the guide.
                    outlined: { borderColor: line.strong }
                },
                // Status as a pale tint with darker text of the same hue, never
                // a solid fill alone: a label says it, colour only backs it up.
                ...(soft && ui.chips === 'soft' ? {
                    variants: Object.entries(statusColours).map(([color, value]) => ({
                        props: { variant: 'filled', color },
                        style: {
                            backgroundColor: tintOf(value),
                            color: textShade(value, tintOf(value)),
                            '& .MuiChip-icon, & .MuiChip-deleteIcon': { color: 'inherit' }
                        }
                    }))
                } : {})
            },

            MuiTableHead: {
                styleOverrides: {
                    root: {
                        // Replaces the flat blue band the app used to paint on
                        // every table. The guide has no such band; a tinted
                        // surface with a strong rule underneath is its idiom.
                        backgroundColor: surface3,
                        '& .MuiTableCell-head': {
                            ...label,
                            fontSize: plainLabels ? 12.5 : 11,
                            color: textMeta,
                            borderBottom: `1px solid ${soft ? line.soft : line.strong}`
                        }
                    }
                }
            },

            MuiTableCell: {
                styleOverrides: {
                    root: soft
                        ? { borderBottom: `1px solid ${line.soft}`, padding: '14px 16px' }
                        : { borderBottom: `1px solid ${line.soft}` }
                }
            },

            MuiTableRow: {
                styleOverrides: {
                    root: {
                        transition: `background-color ${t.fast}ms ${t.easing}`,
                        // A faint purple on hover, a paler purple with a rule
                        // down its edge when selected.
                        ...(soft ? {
                            '&.MuiTableRow-hover:hover': { backgroundColor: alpha(t.p500, 0.04) },
                            '&.Mui-selected, &.Mui-selected:hover': {
                                backgroundColor: alpha(t.p500, 0.09),
                                boxShadow: `inset 3px 0 0 ${t.p500}`
                            }
                        } : {})
                    }
                }
            },

            MuiOutlinedInput: {
                styleOverrides: {
                    root: soft ? {
                        borderRadius: ui.radius.input,
                        backgroundColor: surface2,
                        // A normal field is as tall as a button; a small one,
                        // used in dense toolbars, stays small.
                        '&:not(.MuiInputBase-sizeSmall):not(.MuiInputBase-multiline)': { minHeight: ui.controlHeight },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: line.field },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: mix(line.field, t.inkOnPaper, 0.35) },
                        // The border turns to the focus colour and a soft ring
                        // stands around it, so focus is seen without relying on
                        // the border's colour alone.
                        '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(focusMain, 0.18)}` },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: focusMain, borderWidth: 1 },
                        '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: t.danger },
                        '&.Mui-error.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(t.danger, 0.16)}` },
                        '&.Mui-disabled': { backgroundColor: surface3 }
                    } : {
                        borderRadius: t.r2,
                        backgroundColor: dark ? 'rgba(255,255,255,0.03)' : 'rgba(13,27,42,0.03)',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: line.soft },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: line.strong },
                        // The guide asks for the border itself to become cyan on
                        // focus, with no second ring around it.
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: t.c400,
                            borderWidth: 1
                        }
                    },
                    input: { fontSize: soft ? 15 : 14 }
                }
            },

            MuiInputLabel: {
                styleOverrides: {
                    root: { fontSize: 14, '&.Mui-focused': { color: soft ? focusMain : accentMain } }
                }
            },

            MuiAlert: {
                styleOverrides: {
                    root: { borderRadius: t.r2, border: `1px solid ${line.soft}` }
                }
            },

            MuiDialog: {
                styleOverrides: {
                    paper: { borderRadius: soft ? ui.radius.dialog : t.r4, backgroundImage: 'none' }
                }
            },

            MuiTab: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: 14.5,
                        minHeight: 44
                    }
                }
            },

            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        fontFamily: monoStack,
                        fontSize: plainLabels ? 12 : 11,
                        letterSpacing: plainLabels ? 0 : '0.04em',
                        backgroundColor: dark ? t.bg3 : t.inkOnPaper,
                        border: `1px solid ${line.strong}`
                    }
                }
            },

            MuiLinearProgress: {
                styleOverrides: { root: { borderRadius: 999, height: 8 } }
            },

            MuiDrawer: {
                styleOverrides: { paper: { backgroundImage: 'none', border: 'none' } }
            },

            MuiAppBar: {
                styleOverrides: { root: { backgroundImage: 'none', border: 'none' } }
            }
        }
    };
};

export default buildTheme;

/**
 * The Dataflix palette extension, with a fallback.
 *
 * `palette.dataflix` only exists on a theme built here. A component that reads
 * it directly crashes under any other theme — including MUI's default, which is
 * what a test renders with if it forgets the provider. Reading through this
 * keeps a missing extension to a wrong colour rather than a blank screen.
 */
export const dataflixPalette = (theme) =>
    theme?.palette?.dataflix || {
        surface: tokens.bg,
        surface2: tokens.bg2,
        surface3: tokens.bg3,
        line: 'rgba(255,255,255,0.07)',
        lineStrong: 'rgba(255,255,255,0.16)',
        meta: tokens.ink3,
        accent: tokens.c400,
        gradient1: tokens.gradient1,
        gradient2: tokens.gradient2,
        mono: FONT_MONO,
        serif: FONT_SERIF,
        navBg: tokens.bg2,
        navText: tokens.ink,
        navMeta: tokens.ink3
    };
